// src/hooks/useTranslations.js
import { HEBREW_TRANSLATIONS } from '../constants/translations';

export function useTranslations() {
  return HEBREW_TRANSLATIONS;
}

// utils/campaignTranslations.js - Utility functions for campaign translations

/**
 * Get translated campaign type
 * @param {string} type - Campaign type ('One-Time')
 * @param {Object} t - Translation function
 * @returns {string} Translated type
 */
export function getTranslatedCampaignType(type, t) {
  const typeMap = {
    'One-Time': t.oneTime,
  };
  return typeMap[type] || type;
}

/**
 * Format date for Hebrew locale
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date in Hebrew
 */
export function formatDateHebrew(date) {
  // Return empty string for null, undefined, or empty values
  if (!date) return '';
  console.log("DATE RECEIVED:");
  console.log(date, typeof date);
  // Handle string dates
  let dateObj;
  if (typeof date === 'string') {
    // Handle empty string
    if (date.trim() === '') return '';
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    // Invalid type - return empty string
    console.warn('formatDateHebrew: Invalid date type received:', typeof date, date);
    return '';
  }
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    console.warn('formatDateHebrew: Invalid date value received:', date);
    return '';
  }
  
  try {
    return dateObj.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('formatDateHebrew: Error formatting date:', error, date);
    return '';
  }
}

/**
 * Get translated campaign types for dropdowns
 * @param {Object} t - Translation function
 * @returns {Array} Array of {value, label} objects
 */
export function getTranslatedCampaignTypes(t) {
  return [
    { value: 'One-Time', label: t.oneTime }
  ];
}

/**
 * Translate customer activity status from English to Hebrew
 * @param {string} status - English status ('Active', 'Inactive', 'Dormant')
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateActivityStatus(status, t) {
  return t.customerStatsTranslations?.activityStatus?.[status] || status;
}

/**
 * Translate customer value description from English to Hebrew
 * @param {string} valueDesc - English value description
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateValueDescription(valueDesc, t) {
  return t.customerStatsTranslations?.valueDescription?.[valueDesc] || valueDesc;
}

/**
 * Translate customer frequency description from English to Hebrew
 * @param {string} frequencyDesc - English frequency description
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateFrequencyDescription(frequencyDesc, t) {
  return t.customerStatsTranslations?.frequencyDescription?.[frequencyDesc] || frequencyDesc;
}

/**
 * Translate customer tenure description from English to Hebrew
 * @param {string} tenureDesc - English tenure description
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateTenureDescription(tenureDesc, t) {
  return t.customerStatsTranslations?.tenureDescription?.[tenureDesc] || tenureDesc;
}

/**
 * Format days since last visit in Hebrew
 * @param {number} days - Number of days since last visit
 * @param {Object} t - Translation object
 * @returns {string} Formatted Hebrew text
 */
export function formatDaysSinceLastVisit(days, t) {
  if (!days && days !== 0) return t.statsLabels?.never || 'מעולם לא';
  
  if (days === 0) return t.statsLabels?.today || 'היום';
  if (days === 1) return t.statsLabels?.yesterday || 'אתמול';
  
  return `${days} ${t.statsLabels?.daysAgo || 'ימים'}`;
}

/**
 * Translate suggested action from English to Hebrew
 * @param {string} action - English action text
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateSuggestedAction(action, t) {
  return t.suggestedActions?.actions?.[action] || action;
}

/**
 * Translate action priority from English to Hebrew
 * @param {string} priority - English priority ('high', 'medium', 'low')
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateActionPriority(priority, t) {
  return t.suggestedActions?.priorities?.[priority] || priority;
}

/**
 * Translate action reason from English to Hebrew
 * @param {string} reason - English reason text
 * @param {Object} t - Translation object
 * @returns {string} Hebrew translation
 */
export function translateActionReason(reason, t) {
  // Handle dynamic reasons with numbers
  if (reason.includes('days since last visit')) {
    const daysMatch = reason.match(/(\d+) days since last visit/);
    if (daysMatch) {
      const days = daysMatch[1];
      return `${days} ${t.suggestedActions?.reasonTemplates?.['days since last visit'] || 'ימים מאז הביקור האחרון'}`;
    }
  }
  
  if (reason.includes('Low completion rate:')) {
    const percentMatch = reason.match(/Low completion rate: (\d+)%/);
    if (percentMatch) {
      const percent = percentMatch[1];
      return `${t.suggestedActions?.reasonTemplates?.['Low completion rate'] || 'שיעור השלמה נמוך'}: ${percent}%`;
    }
  }
  
  // Check for exact template matches
  const templates = t.suggestedActions?.reasonTemplates || {};
  for (const [englishTemplate, hebrewTemplate] of Object.entries(templates)) {
    if (reason.includes(englishTemplate)) {
      return hebrewTemplate;
    }
  }
  
  return reason; // Fallback to original if no translation found
}